import React, { useCallback } from 'react';
import { Container, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ClaimsProvider, useClaims } from '../../contexts/ClaimsContext';
import useSnackbar from '../../hooks/useSnackbar';
import CMS1500FormComponent from '../../components/CMS1500Form/CMS1500Form';
import PDFViewer from '../../components/PDFViewer/PDFViewer';
import SearchCMS1500Claims from '../../components/CMS1500Form/SearchCMS1500Claims';

// Import custom hooks
import useCMS1500FormManagement from './hooks/useCMS1500FormManagement';
import useUnsavedChanges from './hooks/useUnsavedChanges';
import useViewMode from './hooks/useViewMode';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';

// Import components
import FormTabs from './components/FormTabs';
import ViewModeToggle from './components/ViewModeToggle';
import UnsavedChangesDialog from './components/UnsavedChangesDialog';
import NoFormsPlaceholder from './components/NoFormsPlaceholder';
import PageHeader from './components/PageHeader';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  formContainer: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * CMS-1500 Manual Ingest page component
 */
const CMS1500IngestPageInternal: React.FC = () => {
  const classes = useStyles();
  const showSnackbar = useSnackbar();
  const { selectedClaimId } = useClaims();

  // Initialize hooks
  const { viewMode, setMode } = useViewMode('form');

  const {
    getCurrentForm,
    getCurrentForms,
    getCurrentFormIndex,
    changeFormIndex,
    handleFormChange,
    addNewForm,
    selectClaim,
    saveCurrentForm,
    handleProceedWithAction,
  } = useCMS1500FormManagement({ showSnackbar, selectedClaimId });

  const {
    unsavedChanges,
    setHasUnsavedChanges,
    showUnsavedDialog,
    handlePotentiallyBlockedAction,
    handleDialogAction,
    cancelPendingAction,
  } = useUnsavedChanges();

  // Get current form and forms
  const currentForm = getCurrentForm();
  const currentForms = getCurrentForms();
  const currentFormIndex = getCurrentFormIndex();

  // Set up keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'b',
      ctrlKey: true,
      handler: () => {
        // Only toggle if we have a form with a PDF
        if (currentForm && currentForm.fileUrl) {
          setMode(viewMode === 'form' ? 'pdf' : 'form');
        }
      },
    },
  ]);

  // Handle form tab change
  const handleFormTabChange = useCallback(
    (event: React.ChangeEvent<unknown>, newValue: number) => {
      const action = {
        type: 'changeFormTab',
        payload: { index: newValue },
      };
      if (handlePotentiallyBlockedAction(action)) {
        changeFormIndex(newValue);
      }
    },
    [handlePotentiallyBlockedAction, changeFormIndex]
  );

  // Handle adding a new form
  const handleAddForm = useCallback(() => {
    const action = { type: 'addForm' };
    if (handlePotentiallyBlockedAction(action)) {
      addNewForm();
    }
  }, [handlePotentiallyBlockedAction, addNewForm]);

  // Handle selecting a CMS-1500 claim from search
  const handleSelectClaim = useCallback(
    (selectedForm) => {
      const action = { type: 'selectClaim', payload: [selectedForm] };
      if (handlePotentiallyBlockedAction(action)) {
        selectClaim(selectedForm);
        setHasUnsavedChanges(false);
      }
    },
    [handlePotentiallyBlockedAction, selectClaim, setHasUnsavedChanges]
  );

  // Handle form changes
  const handleFormChangeWrapper = useCallback(
    (updatedForm) => {
      console.log({ updatedForm });
      const changed = handleFormChange(updatedForm);
      if (changed) {
        setHasUnsavedChanges(true);
      }
    },
    [handleFormChange, setHasUnsavedChanges]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      await saveCurrentForm();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving CMS-1500 form:', error);
    }
  }, [saveCurrentForm, setHasUnsavedChanges]);

  // Handle dialog actions
  const handleSaveDialog = useCallback(() => {
    handleDialogAction(true, saveCurrentForm, handleProceedWithAction);
  }, [handleDialogAction, saveCurrentForm, handleProceedWithAction]);

  const handleDiscardDialog = useCallback(() => {
    handleDialogAction(false, saveCurrentForm, handleProceedWithAction);
  }, [handleDialogAction, saveCurrentForm, handleProceedWithAction]);

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (mode: 'form' | 'pdf') => {
      setMode(mode);
    },
    [setMode]
  );

  return (
    <Container maxWidth="xl" className={classes.root}>
      <PageHeader
        formType="cms1500"
        hasUnsavedChanges={unsavedChanges}
        hasForms={currentForms.length > 0}
        isManuallyApproved={currentForm?.isCheckedManually}
        onSave={handleSave}
        onAddNewForm={handleAddForm}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <SearchCMS1500Claims onSelectClaim={handleSelectClaim} />
        </Grid>

        <Grid item xs={12} md={9}>
          {currentForms.length > 0 ? (
            <>
              <FormTabs
                forms={currentForms}
                currentIndex={currentFormIndex}
                onTabChange={handleFormTabChange}
              />

              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                pdfAvailable={!!currentForm?.fileUrl}
              />

              <div className={classes.formContainer}>
                {viewMode === 'form' ? (
                  currentForm && (
                    <CMS1500FormComponent
                      formData={currentForm as any}
                      onFormChange={handleFormChangeWrapper}
                      onSave={handleSave}
                    />
                  )
                ) : currentForm?.fileUrl ? (
                  <PDFViewer pdfUrl={currentForm.fileUrl} />
                ) : (
                  <NoFormsPlaceholder onAddNewForm={handleAddForm} />
                )}
              </div>
            </>
          ) : (
            <NoFormsPlaceholder onAddNewForm={handleAddForm} />
          )}
        </Grid>
      </Grid>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onSave={handleSaveDialog}
        onDiscard={handleDiscardDialog}
        onCancel={cancelPendingAction}
      />
    </Container>
  );
};

/**
 * Wrapper component that provides the ClaimsContext
 */
export const CMS1500IngestPage: React.FC = () => {
  return (
    <ClaimsProvider>
      <CMS1500IngestPageInternal />
    </ClaimsProvider>
  );
};
